import React, { FC, PropsWithChildren } from "react";
import styled, { css, keyframes } from "styled-components";
import { propStyling, tombac } from "../../../shared";
import { Container as ButtonContainer } from "./Container";

export type HoverAnimationVariant =
  | "anim-up"
  | "anim-forward"
  | "anim-backward";

export type HoverAnimationProps = PropsWithChildren<{
  delay?: string;
  variant: HoverAnimationVariant | string;
}>;

export const HoverAnimation: FC<HoverAnimationProps> = (props) => {
  const { children, variant } = props;
  if (!variant.startsWith("anim-")) return <>{children}</>;
  return (
    <Container>
      <Content {...props} />
      <Clone aria-hidden {...props} />
    </Container>
  );
};

const backwardDuration = "1s";
const forwardDuration = "1s";
const upDuration = "0.75s";

const easeOutExp = "cubic-bezier(0.16, 1, 0.3, 1)";
const easeOutQuad = "cubic-bezier(0.45, 0, 0.55, 1)";

const up = keyframes`
  0% {
    opacity: 1;
  }
  40%,
  100% {
    opacity: 0;
  }
`;

const upClone = keyframes`
  0% {
    opacity: 0;
  }
  40%,
  100% {
    opacity: 1;
  }
`;

const forward = keyframes`
  0% {
    opacity: 1;
    transform: translateX(0);
  }
  25% {
    opacity: 0;
  }
  100% {
    opacity: 0;
    transform: translateX(100%);
  }
`;

const forwardClone = keyframes`
  0% {
    opacity: 0;
    transform: translateX(-100%);
  }
  25% {
    opacity: 0;
  }
  50% {
    opacity: 1;
  }
  100% {
    opacity: 1;
    transform: translateX(0);
  }
`;

const backward = keyframes`
  0% {
    opacity: 1;
    transform: translateX(0);
  }
  25% {
    opacity: 0;
  }
  100% {
    opacity: 0;
    transform: translateX(-100%);
  }
`;

const backwardClone = keyframes`
  0% {
    opacity: 0;
    transform: translateX(100%);
  }
  25% {
    opacity: 0;
  }
  50% {
    opacity: 1;
  }
  100% {
    opacity: 1;
    transform: translateX(0);
  }
`;

const delay = ({ delay }: HoverAnimationProps) => delay;

const Container = styled.span`
  height: 100%;
  position: relative;
`;

const Content = styled.span<HoverAnimationProps>`
  align-items: center;
  display: flex;
  height: 100%;
  transition-delay: 0;
  transition-duration: 0;
  transition-property: transform;
  transition-timing-function: ${easeOutExp};

  ${ButtonContainer}:not([disabled]):hover && {
    ${tombac.variant("variant", {
      "anim-backward": css`
        animation: ${backward} ${backwardDuration} ${easeOutExp} ${delay}
          infinite;
      `,
      "anim-forward": css`
        animation: ${forward} ${forwardDuration} ${easeOutExp} ${delay} infinite;
      `,
      "anim-up": css`
        animation: ${up} ${upDuration} ${easeOutQuad} ${delay} forwards;
        transform: translateY(-40%);
        transition-delay: ${delay};
        transition-duration: 0.75s;
      `,
    })}
  }
`;

const Clone = styled(Content)`
  display: flex;
  left: 0;
  opacity: 0;
  position: absolute;
  top: 0;
  transform: translateY(40%);

  ${ButtonContainer}:not([disabled]):hover && {
    ${tombac.variant("variant", {
      "anim-backward": css`
        animation: ${backwardClone} ${backwardDuration} ${easeOutExp} ${delay}
          infinite;
      `,
      "anim-forward": css`
        animation: ${forwardClone} ${forwardDuration} ${easeOutExp} ${delay}
          infinite;
      `,
      "anim-up": css`
        animation: ${upClone} ${upDuration} ${easeOutQuad} ${delay} forwards;
        transform: translateY(0);
      `,
    })}
  }

  ${propStyling}
`;
